"use client";

import { useEffect, useState } from "react";
import { isInstalledPwa, isIosDevice } from "@/lib/device";
import { detectDevice } from "@/utils/mobile-detection";

// Disponibilidad de instalación de la PWA, compartida entre la app móvil
// (`InstallPromptProvider`) y el admin (`AdminInstallBanner`). Solo resuelve
// *si* se puede instalar y *cómo*; cada una pone su propia UI.
//
// - "native": el navegador disparó `beforeinstallprompt` (Chrome/Edge, desktop
//   y Android) y el diálogo nativo se puede abrir desde código.
// - "ios": Safari en iPhone/iPad no tiene ese evento — solo queda explicar el
//   paso manual (Compartir → Agregar a inicio).
// Cualquier otro navegador (Firefox, Safari macOS) queda en `null`: no hay
// forma confiable de ofrecer la instalación, así que no se muestra nada.
//
// El descarte se recuerda por localStorage con una key por app: en el mismo
// origen conviven admin y /mobile, y descartar una no debe silenciar la otra.

export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export type InstallPlatform = "ios" | "native";

function wasDismissedRecently(key: string, days: number): boolean {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return false;
    const dismissedAt = Number(raw);
    if (!Number.isFinite(dismissedAt)) return false;
    const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
    return daysSince < days;
  } catch {
    return false;
  }
}

function rememberDismissal(key: string) {
  try {
    window.localStorage.setItem(key, String(Date.now()));
  } catch {
    // Sin storage disponible, el banner vuelve a aparecer la próxima vez — no es grave.
  }
}

export function useInstallAvailability({
  dismissKey,
  dismissDays = 7,
}: {
  dismissKey: string;
  dismissDays?: number;
}) {
  const [deferredEvent, setDeferredEvent] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [platform, setPlatform] = useState<InstallPlatform | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // El banner es para instalar la app en el dispositivo que se usa a diario:
    // nunca tiene sentido en escritorio, aunque Chrome/Edge también disparen
    // `beforeinstallprompt` ahí. Por user-agent, no por ancho de ventana — así
    // angostar una ventana de escritorio no lo hace aparecer.
    if (detectDevice().isDesktop) return;

    if (isInstalledPwa()) return;

    if (isIosDevice()) {
      setPlatform("ios");
      return;
    }

    function onBeforeInstallPrompt(event: Event) {
      event.preventDefault();
      setDeferredEvent(event as BeforeInstallPromptEvent);
      setPlatform("native");
    }

    // Instalada por fuera del banner (ícono de la barra de direcciones): el
    // evento guardado ya no sirve y no hay nada más que ofrecer.
    function onAppInstalled() {
      setDeferredEvent(null);
      setPlatform(null);
    }

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  useEffect(() => {
    setDismissed(wasDismissedRecently(dismissKey, dismissDays));
  }, [dismissKey, dismissDays]);

  function dismiss() {
    rememberDismissal(dismissKey);
    setDismissed(true);
  }

  async function triggerNativeInstall() {
    if (!deferredEvent) return;
    await deferredEvent.prompt();
    await deferredEvent.userChoice;
    setDeferredEvent(null);
    // El navegador ya resolvió la elección (instaló o no) — no tiene sentido
    // volver a ofrecer el banner en esta sesión.
    dismiss();
  }

  // Sin el evento nativo no hay forma de disparar la instalación (ni de
  // explicarla de forma confiable) fuera de iOS.
  const canInstall =
    platform === "ios" || (platform === "native" && !!deferredEvent);

  return { platform, canInstall, dismissed, dismiss, triggerNativeInstall };
}
